import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { useState, useEffect, useRef } from "react";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
export default function RevenueCalculator() {
  const [inputMode, setInputMode] = useState("auto-fetch");
  const [socialMediaLink, setSocialMediaLink] = useState("");
  const [manualViews, setManualViews] = useState("");
  const [services, setServices] = useState({
    pay: true,
    boost: true,
    shield: true,
  });
  const [channelData, setChannelData] = useState(null);
  const [calculatedAvgViews, setCalculatedAvgViews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [earnings, setEarnings] = useState({
    boost: 0,
    shield: 0,
  });
  const [period, setPeriod] = useState("month"); // "month" | "year"
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
  const periodDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(e.target)) {
        setPeriodDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleService = (service) => {
    setServices((prev) => ({
      ...prev,
      [service]: !prev[service],
    }));
  };

  // Shared calculation function for both auto-fetch and manual input
  const calculateRevenue = (ytViews) => {
    // Ensure we have a valid number
    const YT_views = Number(ytViews);
    
    if (isNaN(YT_views) || YT_views <= 0) {
      return {
        boost: 0,
        shield: 0,
      };
    }

    // MSN Revenue Calculator Logic (x5 Variant)
    // Step 1: YT_views (average views per video)
    
    // Step 2: Calculate MSN views (amplification factor = 5)
    // MSN_views = YT_views × 5
    const MSN_views = YT_views * 5;
    
    // Step 3: Calculate estimated revenue for EnfiniBoost (only if enabled)
    // Boost Revenue = (YT_views × 7.50) ÷ 1000 — content distribution rate $7.50 per 1000 views
    const boostRatePer1000 = 7.50;
    const boostRevenue = services.boost ? (YT_views * boostRatePer1000) / 1000 : 0;
    
    // Step 4: Calculate EnfiniShield revenue (only if enabled)
    // Shield Revenue = (MSN_views ÷ 1000) × 0.015 — copyright protection CPM = 0.015 USD
    const shieldRevenue = services.shield ? (MSN_views / 1000) * 0.015 : 0;
    
    return {
      boost: boostRevenue,
      shield: shieldRevenue,
    };
  };

  const calculateManualRevenue = (ytViews) => {
    // Remove commas and parse the number
    const views = parseFloat(ytViews.toString().replace(/,/g, ''));
    
    if (isNaN(views) || views <= 0) {
      setEarnings({ boost: 0, shield: 0 });
      return;
    }

    // Use shared calculation function
    const earnings = calculateRevenue(views);
    setEarnings(earnings);
  };

  const fetchChannelData = async () => {
    if (!socialMediaLink.trim()) {
      setError("Please enter a YouTube channel link");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const encodedLink = encodeURIComponent(socialMediaLink);
      const apiUrl = `https://enfinicore-staging-be-ctcdc0b7drb5evek.canadacentral-01.azurewebsites.net/api/youtube/channel/views-by-link?channel_link=${encodedLink}`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error("Failed to fetch channel data");
      }

      const data = await response.json();
      setChannelData(data);

      // Calculate revenue based on MSN Revenue Calculator Logic (x5 Variant)
      if (data.views && data.video_count && data.video_count > 0) {
        // Step 1: Calculate YouTube average views per video (YT_views)
        // Convert to numbers to ensure proper calculation
        const totalViews = Number(data.views);
        const videoCount = Number(data.video_count);
        const YT_views = totalViews / videoCount;
        
        // Store calculated average views for display
        setCalculatedAvgViews(YT_views);
        
        // Use shared calculation function
        const earnings = calculateRevenue(YT_views);
        setEarnings(earnings);
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching channel data");
      setChannelData(null);
      setCalculatedAvgViews(null);
      setEarnings({ boost: 0, shield: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Recalculate earnings when services change
  useEffect(() => {
    if (inputMode === "auto-fetch" && channelData && calculatedAvgViews) {
      const earnings = calculateRevenue(calculatedAvgViews);
      setEarnings(earnings);
    } else if (inputMode === "manual" && manualViews) {
      const views = parseFloat(manualViews.toString().replace(/,/g, ''));
      if (!isNaN(views) && views > 0) {
        const earnings = calculateRevenue(views);
        setEarnings(earnings);
      }
    }
  }, [services, inputMode, channelData, calculatedAvgViews, manualViews]);

  // Display values: per year = monthly * 12 * 1.35 (35% YoY growth)
  const displayEarnings = period === "year"
    ? { boost: earnings.boost * 12 * 1.35, shield: earnings.shield * 12 * 1.35 }
    : { boost: earnings.boost, shield: earnings.shield };

  return (
    <div
      id="core-services"
      className="bg-white pt-4 xxs:-pt-0 xs:pt-0 sm:pt-0 md:pt-8 lg:pt-10 pb-20 px-4 lg:px-6 xl:px-8 "
    >
      {/* Header Section */}
      <div className="mb-12 text-center max-w-4xl mx-auto">
        <p className="text-black/60 text-xs xxs:text-xs xs:text-sm sm:text-sm md:text-md 4xl:text-[24px] lg:text-lg font-medium mb-4enfinity-subtext-font">
        Revenue Calculator
        </p>
        <h2 className="text-3xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal mb-4 enfinity-header-font" style={{lineHeight: '1.2'}}>
          <span className="text-black enfinity-header-font">See your </span>
          <span
            className="bg-gradient-to-r from-[#00D115] via-[#00B0FF] to-[#9C44E9] bg-clip-text text-transparent enfinity-header-font"
          >
            potential earnings
          </span>
          <span className="text-black enfinity-header-font"> with Enfinity</span>
        </h2>
      </div>

      {/* Main Calculator Card */}
      <div className="bg-[#FBFBFB] rounded-3xl p-6 md:p-8 lg:p-10 mb-8 xxs:max-w-[550px] xs:max-w-4xl max-w-4xl mx-auto border border-[#dddddd] shadow-[0_4px_6px_rgba(0,0,0,0.1)] relative overflow-hidden">
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-[#dddddd] -translate-x-1/2" style={{ top: '-1.5rem', bottom: '-1.5rem' }}></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 lg:items-stretch">
          <div className="lg:pr-8 flex flex-col">
          {/* Left Panel - Input and Service Selection */}
          <div className="flex flex-col gap-6">
            {/* Mode Switch */}
            <div className="flex gap-2 bg-white rounded-xl p-1 border border-gray-200">
              <button
                onClick={() => {
                  setInputMode("auto-fetch");
                  // Reset earnings when switching to auto-fetch
                  setEarnings({ boost: 0, shield: 0 });
                  setError(null);
                }}
                className={`flex-1 py-2 px-4 rounded-xl text-md font-normal transition-colors enfinity-subtext-font ${
                  inputMode === "auto-fetch"
                    ? "bg-[#F0F0F0] text-gray-900"
                    : "text-black"
                }`}
              >
                Auto-Fetch
              </button>
              <button
                onClick={() => {
                  setInputMode("manual");
                  // Reset channel data when switching to manual
                  setChannelData(null);
                  setError(null);
                  // Calculate with current manual input if available
                  if (manualViews) {
                    calculateManualRevenue(manualViews);
                  }
                }}
                className={`flex-1 py-2 px-4 rounded-xl text-md font-normal transition-colors enfinity-subtext-font ${
                  inputMode === "manual"
                    ? "bg-[#f0f0f0] text-gray-900"
                    : "text-black"
                }`}
              >
                Manual Input
              </button>
            </div>

            {/* Input Fields */}
            {inputMode === "auto-fetch" ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-4">
                  <label className="text-sm font-medium text-gray-700 enfinity-subtext-font">
                    Your social media link
                  </label>
                  <span className="text-sm font-medium text-gray-700 enfinity-subtext-font shrink-0 mr-[95px]">Per</span>
                </div>
                <div className="flex gap-2 items-stretch">
                  <input
                    type="text"
                    value={socialMediaLink}
                    onChange={(e) => setSocialMediaLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        fetchChannelData();
                      }
                    }}
                    className="flex-1 min-w-0 text-sm px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 enfinity-subtext-font"
                    placeholder="https://youtube.com/@yourchannel"
                  />
                  <div className="relative w-[120px] shrink-0" ref={periodDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setPeriodDropdownOpen((o) => !o)}
                      className="w-full text-sm font-normal text-gray-400 enfinity-subtext-font pl-3 pr-8 py-3 rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer h-full text-left flex items-center justify-between"
                    >
                      {period === "year" ? "Year" : "Month"}
                      <ChevronDown className={`absolute right-2 w-4 h-4 text-gray-500 transition-transform ${periodDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {periodDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-white rounded-xl border border-gray-200 shadow-lg z-50">
                        <button
                          type="button"
                          onClick={() => { setPeriod("month"); setPeriodDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm enfinity-subtext-font hover:bg-gray-50 transition-colors ${period === "month" ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700"}`}
                        >
                          Month
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPeriod("year"); setPeriodDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm enfinity-subtext-font hover:bg-gray-50 transition-colors rounded-b-xl ${period === "year" ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700"}`}
                        >
                          Year
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {error && (
                  <p className="text-sm text-red-500 enfinity-subtext-font">{error}</p>
                )}
                {channelData && (
                  <div className="space-y-1">
                    <p className="text-sm text-green-600 enfinity-subtext-font">
                      Channel: {channelData.channel_title} • {channelData.subscribers?.toLocaleString()} subscribers
                    </p>
                    {calculatedAvgViews && (
                      <p className="text-sm text-gray-600 enfinity-subtext-font">
                        Avg views per video: {Math.round(calculatedAvgViews).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full">
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-4">
                    <label className="text-sm font-medium text-gray-700 enfinity-subtext-font">
                      Average Views per Video
                    </label>
                    <span className="text-sm font-medium text-gray-700 enfinity-subtext-font shrink-0 mr-[95px]">Per</span>
                  </div>
                  <div className="flex gap-2 items-stretch">
                    <input
                      type="text"
                      value={manualViews}
                      onChange={(e) => {
                        const value = e.target.value;
                        setManualViews(value);
                        setError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (!manualViews.trim()) {
                            setError("Please enter average views per video");
                            return;
                          }
                          calculateManualRevenue(manualViews);
                        }
                      }}
                      className="flex-1 min-w-0 text-sm px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 enfinity-subtext-font"
                      placeholder="Ex. 785,000"
                    />
                    <div className="relative w-[120px] shrink-0" ref={periodDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setPeriodDropdownOpen((o) => !o)}
                        className="w-full text-sm font-normal text-gray-900 enfinity-subtext-font pl-3 pr-8 py-3 rounded-xl border border-gray-300 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer h-full text-left flex items-center justify-between"
                      >
                        {period === "year" ? "Year" : "Month"}
                        <ChevronDown className={`absolute right-2 w-4 h-4 text-gray-500 transition-transform ${periodDropdownOpen ? "rotate-180" : ""}`} />
                      </button>
                      {periodDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-white rounded-xl border border-gray-200 shadow-lg z-50">
                          <button
                            type="button"
                            onClick={() => { setPeriod("month"); setPeriodDropdownOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm enfinity-subtext-font hover:bg-gray-50 transition-colors ${period === "month" ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700"}`}
                          >
                            Month
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPeriod("year"); setPeriodDropdownOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm enfinity-subtext-font hover:bg-gray-50 transition-colors rounded-b-xl ${period === "year" ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700"}`}
                          >
                            Year
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {error && inputMode === "manual" && (
                    <p className="text-sm text-red-500 enfinity-subtext-font">{error}</p>
                  )}
                </div>
              </div>
            )}

            {/* Service Toggles */}
            <div className="flex flex-col gap-3">
              {/* EnfiniPay */}
              <div className="flex items-center justify-between px-4 py-3 bg-[#ffffff] border border-[#dddddd] rounded-xl">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-9 h-9 rounded flex items-center justify-center p-2"
                    style={{ background: 'linear-gradient(to bottom, #00A656, #099129, #127E00)' }}
                  >
                    <img src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/pay.svg" style={{ filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="font-semibold text-gray-900 enfinity-subtext-font">EnfiniPay</p>
                    <p className="text-sm text-gray-500 enfinity-subtext-font">Flexible payment options</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleService("pay")}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    services.pay ? "bg-[#00D115]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      services.pay ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* EnfiniBoost */}
              <div className="flex items-center justify-between px-4 py-3 bg-[#ffffff] border border-[#dddddd] rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-9 h-9 rounded flex items-center justify-center p-2"
                    style={{ background: 'linear-gradient(to bottom, #60A5FA, #3B82F6)' }}
                  >
                    <img src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/boost.svg" style={{ filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="font-semibold text-gray-900 enfinity-subtext-font">EnfiniBoost</p>
                    <p className="text-sm text-gray-500 enfinity-subtext-font">Content distribution</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleService("boost")}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    services.boost ? "bg-[#00B0FF]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      services.boost ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* EnfiniShield */}
              <div className="flex items-center justify-between px-4 py-3 bg-[#ffffff] border border-[#ddddddd] rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-9 h-9 rounded flex items-center justify-center p-2"
                    style={{ background: 'linear-gradient(to bottom, #A78BFA, #8B5CF6)' }}
                  >
                    <img src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/shield-violet.svg" style={{ filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="font-semibold text-gray-900 enfinity-subtext-font">EnfiniShield</p>
                    <p className="text-sm text-gray-500 enfinity-subtext-font">Copyright protection</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleService("shield")}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    services.shield ? "bg-[#9C44E9]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      services.shield ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Analyze Button */}
            <div 
              className="rounded-3xl p-[2px] w-full transition-all duration-300 hover:shadow-[0_0_10px_rgba(0,176,255,0.4),0_0_15px_rgba(0,176,255,0.3),0_0_20px_rgba(0,176,255,0.2)]"
              style={{
                background: 'linear-gradient(to right, #00A656, #14b8a6, #8b5cf6, #ec4899, #f59e0b)'
              }}
            >
              <EnfinityPrimaryBtn
                className="w-full hover:translate-y-0 hover:shadow-none"
                onClick={() => {
                  if (inputMode === "auto-fetch") {
                    fetchChannelData();
                  } else {
                    if (!manualViews.trim()) {
                      setError("Please enter average views per video");
                      return;
                    }
                    calculateManualRevenue(manualViews);
                  }
                }}
                disable={loading}
                noBorder={true}
              >
                <span className="flex items-center justify-center gap-2">
                  {loading ? "Analyzing..." : "Analyze channel with AI"}
                  <Sparkles className="w-4 h-4" />
                </span>
              </EnfinityPrimaryBtn>
            </div>
          </div>
          </div>

          {/* Right Panel - Earnings Display */}
          <div className="flex flex-col gap-4 lg:pl-8 h-full">
            <div>
              <h3 className="text-2xl font-normal text-gray-900 mb-2 enfinity-subtext-font">
                Your potential earnings
              </h3>
              <p className="text-sm text-gray-600 enfinity-subtext-font">
                Based on your channel data and selected services
              </p>
            </div>

            {/* Earnings Cards */}
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/* EnfiniPay Card */}
              <div className={`bg-white rounded-xl p-4 flex-1 flex flex-col justify-center gap-1 border border-[#e5e5e5] transition-opacity min-h-[4.5rem] ${
                !services.pay ? "opacity-50" : ""
              }`}>
                <p className={`text-sm font-medium enfinity-subtext-font ${
                  !services.pay ? "text-gray-400" : "text-gray-600"
                }`}>EnfiniPay</p>
                <p className={`text-2xl font-bold enfinity-subtext-font uppercase tracking-tight ${
                  !services.pay ? "text-gray-400" : "text-[#00D115]"
                }`}>AVAILABLE</p>
                <p className={`text-xs enfinity-subtext-font ${
                  !services.pay ? "text-gray-400" : "text-gray-500"
                }`}>
                  Daily, early, lump sum, fiat, or crypto payments
                </p>
                <img
                  src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/logos-calcu.svg"
                  alt="Payment methods"
                  className="h-6 w-auto object-contain object-left"
                />
              </div>

              {/* EnfiniBoost Card */}
              <div className={`bg-white rounded-xl p-4 flex-1 flex flex-col justify-center gap-1 border border-[#dddddd] transition-opacity min-h-[4.5rem] ${
                !services.boost ? "opacity-50" : ""
              }`}>
                <p className={`font-semibold text-sm enfinity-subtext-font ${
                  !services.boost ? "text-gray-400" : "text-gray-900"
                }`}>EnfiniBoost</p>
                <p className={`text-2xl font-normal enfinity-subtext-font ${
                  !services.boost ? "text-gray-400" : "text-[#00B0FF]"
                }`}>
                  {displayEarnings.boost > 0 
                    ? `+$${displayEarnings.boost.toFixed(2)}` 
                    : "+$0.00"}
                  <span className="text-sm font-normal text-gray-500 ml-1">/{period === "year" ? "year" : "month"}</span>
                </p>
                <p className={`text-xs enfinity-subtext-font ${
                  !services.boost ? "text-gray-400" : "text-black/50"
                }`}>
                  Additional revenue from multi-platform distribution
                </p>
              </div>

              {/* EnfiniShield Card */}
              <div className={`bg-white rounded-xl p-4 flex-1 flex flex-col justify-center gap-1 border border-[#dddddd] transition-opacity min-h-[4.5rem] ${
                !services.shield ? "opacity-50" : ""
              }`}>
                <p className={`font-semibold text-sm enfinity-subtext-font ${
                  !services.shield ? "text-gray-400" : "text-gray-900"
                }`}>EnfiniShield</p>
                <p className={`text-2xl font-normal enfinity-subtext-font ${
                  !services.shield ? "text-gray-400" : "text-[#9C44E9]"
                }`}>
                  {displayEarnings.shield > 0 
                    ? `+$${displayEarnings.shield.toFixed(2)}` 
                    : "+$0.00"}
                  <span className="text-sm font-normal text-gray-500 ml-1">/{period === "year" ? "year" : "month"}</span>
                </p>
                <p className={`text-xs enfinity-subtext-font ${
                  !services.shield ? "text-gray-400" : "text-black/50"
                }`}>
                  Recovered revenue from copyright protection
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA Button */}
      <div className="flex justify-center items-center">
        <a
          href="https://core.enfinity.com/signup"
          target="_blank"
          rel="noopener noreferrer"
        >
          <EnfinityPrimaryBtn className="w-auto">
            <span className="flex items-center gap-2 enfinity-subtext-font">
              Get started with Enfinity
              <ArrowRight className="w-4 h-4" />
            </span>
          </EnfinityPrimaryBtn>
        </a>
      </div>
    </div>
  );
}
